/**
 * A custom element for the cart drawer.
 *
 * This refactored version focuses on performance by avoiding the use of innerHTML
 * for re-rendering and instead performs targeted DOM updates. It also uses
 * requestAnimationFrame for smoother animations.
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    
    // Bind methods to maintain proper 'this' context
    this.boundOpen = this.open.bind(this);
    this.boundClose = this.close.bind(this);
    this.boundKeyUp = this.handleKeyUp.bind(this);
    
    // Set initial state
    this.isInitialized = false;
    this.isOpening = false;
    
    // Initialize the component
    this.initialize();
  }
  
  connectedCallback() {
    if (!this.isInitialized) {
      this.initialize();
    }
  }
  
  disconnectedCallback() {
    this.removeEventListeners();
  }
  
  initialize() {
    if (this.isInitialized) return;
    
    try {
      console.log('Initializing cart drawer...');
      
      // Standard event listeners for closing the drawer
      this.addEventListener('keyup', this.boundKeyUp);
      
      // Set up overlay click handler
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.boundClose);
      } else {
        console.warn('Cart drawer overlay not found');
      }
      
      // Listen for the custom open event
      document.addEventListener('open-cart-drawer', this.boundOpen);
      console.log('Added open-cart-drawer event listener');
      
      // Set up header cart icon accessibility
      this.setHeaderCartIconAccessibility();
      
      this.isInitialized = true;
      console.log('Cart drawer initialized');
    } catch (error) {
      console.error('Error initializing cart drawer:', error);
    }
  }
  
  removeEventListeners() {
    this.removeEventListener('keyup', this.boundKeyUp);
    
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.removeEventListener('click', this.boundClose);
    }
    
    document.removeEventListener('open-cart-drawer', this.boundOpen);
  }
  
  handleKeyUp(evt) {
    if (evt.code === 'Escape') {
      this.close();
    }
  }

  /**
   * Sets up accessibility attributes and event listeners for the cart icon bubble.
   */
  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /**
   * Opens the cart drawer and handles animations and focus trapping.
   * @param {HTMLElement} triggeredBy - The element that triggered the drawer to open.
   */
  open(triggeredBy) {
    // Prevent multiple simultaneous open attempts
    if (this.isOpening) {
      console.log('Cart drawer is already opening');
      return;
    }
    
    this.isOpening = true;
    console.log('CartDrawer open method called with:', triggeredBy);
    
    try {
      // If triggeredBy is an event, prevent default and get the element
      let triggerElement = null;
      if (triggeredBy && typeof triggeredBy.preventDefault === 'function') {
        console.log('Preventing default action on trigger event');
        triggeredBy.preventDefault();
        triggerElement = triggeredBy.target?.closest('button, a') || document.activeElement;
      } else if (triggeredBy?.nodeType === Node.ELEMENT_NODE) {
        console.log('Triggered by element:', triggerElement);
        triggerElement = triggeredBy;
      } else if (triggeredBy) {
        console.log('Unexpected trigger type:', typeof triggeredBy, triggeredBy);
      }
      
      // Store the active element for focus management
      this.activeElement = document.activeElement;
      console.log('Stored active element for focus management:', this.activeElement);
      
      // Close any other open drawers first
      const otherOpenDrawer = document.querySelector('header-drawer[open], menu-drawer[open]');
      
      const openDrawer = () => {
        try {
          console.log('Proceeding to open cart drawer...');
          this._openDrawer(triggerElement);
        } catch (error) {
          console.error('Error in _openDrawer:', error);
          // Fallback to redirect if drawer can't be opened
          console.log('Falling back to cart page redirect');
          window.location.href = '/cart';
        } finally {
          this.isOpening = false;
        }
      };
      
      if (otherOpenDrawer && otherOpenDrawer !== this) {
        console.log('Found other open drawer, closing it first:', otherOpenDrawer.tagName);
        if (typeof otherOpenDrawer.closeMenuDrawer === 'function') {
          console.log('Closing other drawer...');
          otherOpenDrawer.closeMenuDrawer({}, true);
          // Small delay to allow the other drawer to close
          console.log('Waiting for other drawer to close...');
          setTimeout(openDrawer, 150);
        } else {
          console.log('Other drawer does not have closeMenuDrawer method, proceeding to open');
          openDrawer();
        }
      } else {
        console.log('No other open drawers found, proceeding to open');
        openDrawer();
      }
    } catch (error) {
      console.error('Error in cart drawer open method:', error);
      this.isOpening = false;
      console.log('Falling back to cart page redirect due to error');
      window.location.href = '/cart';
    }
  }

  _openDrawer(triggeredBy) {
    console.log('_openDrawer called with:', triggeredBy);
    
    try {
      // Ensure the drawer is in the DOM
      if (!this.isConnected) {
        console.error('Cart drawer is not connected to the DOM');
        throw new Error('Cart drawer element not connected to DOM');
      }
      
      if (triggeredBy) {
        console.log('Setting active element for focus management');
        try {
          this.setActiveElement(triggeredBy);
        } catch (error) {
          console.warn('Could not set active element:', error);
        }
      }

      // Set accessibility for the cart notes section if it exists
      try {
        const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
        if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
          console.log('Setting up cart drawer note accessibility');
          this.setSummaryAccessibility(cartDrawerNote);
        }
      } catch (error) {
        console.warn('Error setting up cart drawer note accessibility:', error);
      }

      // Add active class with animation frame
      console.log('Scheduling animation frame for cart drawer open');
      requestAnimationFrame(() => {
        try {
          console.log('Adding active class and open attribute to cart drawer');
          this.classList.add('animate', 'active');
          this.setAttribute('open', '');
          
          // Dispatch an event that the drawer has started opening
          console.log('Dispatching cart-drawer:opening event');
          this.dispatchEvent(new CustomEvent('cart-drawer:opening', { 
            bubbles: true,
            detail: { triggeredBy }
          }));
          
          // Set up transition end handler
          const handleTransitionEnd = (event) => {
            // Only handle the opacity transition (or another specific property)
            if (event.propertyName !== 'opacity') return;
            
            console.log('Transition end event:', event.propertyName);
            
            try {
              console.log('Setting up focus trap for cart drawer');
              const containerToTrapFocusOn = this.classList.contains('is-empty')
                ? this.querySelector('.drawer__inner-empty')
                : this.querySelector('.drawer__inner') || this;
                
              const focusElement = this.querySelector('.drawer__close') || 
                                 this.querySelector('button:not([disabled])') ||
                                 this.querySelector('button');
              
              if (containerToTrapFocusOn && focusElement) {
                console.log('Setting up focus trap on:', containerToTrapFocusOn);
                trapFocus(containerToTrapFocusOn, focusElement);
              } else {
                console.warn('Could not set up focus trap - missing elements', {
                  container: containerToTrapFocusOn,
                  focusElement: focusElement
                });
              }
              
              // Dispatch an event that the drawer is fully open
              console.log('Dispatching cart-drawer:opened event');
              this.dispatchEvent(new CustomEvent('cart-drawer:opened', { 
                bubbles: true,
                detail: { triggeredBy }
              }));
              
            } catch (error) {
              console.error('Error in transitionend handler:', error);
            } finally {
              this.removeEventListener('transitionend', handleTransitionEnd);
            }
          };
          
          // Listen for transition end
          console.log('Adding transitionend event listener');
          this.addEventListener('transitionend', handleTransitionEnd, { once: true });
          
          // Fallback in case transitionend doesn't fire
          const fallbackTimeout = setTimeout(() => {
            if (this.isConnected) {
              console.log('Fallback: Dispatching cart-drawer:opened event after timeout');
              this.dispatchEvent(new CustomEvent('cart-drawer:opened', { 
                bubbles: true,
                detail: { triggeredBy, fallback: true }
              }));
            }
          }, 1000);
          
          // Clean up the fallback if transitionend fires
          this.addEventListener('transitionend', () => {
            clearTimeout(fallbackTimeout);
          }, { once: true });
          
        } catch (error) {
          console.error('Error in requestAnimationFrame:', error);
          throw error;
        }
      });
      
      // Prevent body scrolling when drawer is open
      document.body.classList.add('overflow-hidden');
      console.log('Added overflow-hidden to body');
      
    } catch (error) {
      console.error('Error in _openDrawer:', error);
      this.isOpening = false;
      console.log('Falling back to cart page redirect due to error in _openDrawer');
      window.location.href = '/cart';
    }
  }

  /**
   * Closes the cart drawer.
   */
  close() {
    if (!this.classList.contains('active')) return;
    
    this.classList.remove('active');
    this.removeAttribute('open');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    
    // Restore focus to the element that opened the drawer
    if (this.activeElement) {
      setTimeout(() => {
        this.activeElement.focus();
        this.activeElement = null;
      }, 10);
    }
  }

  /**
   * Sets up accessibility for the cart notes summary/details element.
   * @param {HTMLElement} cartDrawerNote - The summary element for the cart notes.
   */
  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    // Assuming onKeyUpEscape is a globally available function
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /**
   * --- MAJOR PERFORMANCE REFACTOR ---
   * This function now updates the DOM in a targeted way instead of replacing
   * entire sections with innerHTML. This is far more efficient and avoids
   * blocking the browser's main thread, directly improving the TBT score.
   * @param {object} parsedState - The state object containing the new cart data.
   */
  renderContents(parsedState) {
    if (!parsedState || !parsedState.sections) {
      console.error('Invalid or missing cart data:', parsedState);
      return;
    }

    try {
      // Safely get the cart drawer HTML
      const cartDrawerSection = parsedState.sections['cart-drawer'];
      const cartBubbleSection = parsedState.sections['cart-icon-bubble'];
      
      if (!cartDrawerSection) {
        console.warn('Missing cart-drawer section in response');
        return;
      }

      // Get the new HTML content from the API response
      const newCartDrawerHTML = this.getSectionDOM(cartDrawerSection);
      if (!newCartDrawerHTML) {
        console.error('Failed to parse cart drawer HTML');
        return;
      }

      // 1. Update the Cart Items List
      const newItemsContainer = newCartDrawerHTML.querySelector('.drawer__inner');
      const currentItemsContainer = this.querySelector('.drawer__inner');
      
      if (newItemsContainer && currentItemsContainer) {
        // By only replacing the innerHTML of this specific container, we avoid
        // re-rendering the entire drawer, headers, footers, etc.
        currentItemsContainer.innerHTML = newItemsContainer.innerHTML;
      }

      // 2. Update the Cart Icon Bubble count if available
      if (cartBubbleSection) {
        const newCartBubbleHTML = this.getSectionDOM(cartBubbleSection);
        if (newCartBubbleHTML) {
          const newBubbleContent = newCartBubbleHTML.querySelector('.cart-count-bubble');
          const currentBubbleContent = document.querySelector('#cart-icon-bubble .cart-count-bubble');
          if (newBubbleContent && currentBubbleContent) {
            currentBubbleContent.innerHTML = newBubbleContent.innerHTML;
          }
        }
      }

      // 3. Update the "is-empty" class state
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) {
        const isCartEmpty = newCartDrawerHTML.querySelector('.drawer__inner')?.classList.contains('is-empty') || false;
        drawerInner.classList.toggle('is-empty', isCartEmpty);
      }

      // After adding an item, ensure the drawer is open.
      if (!this.classList.contains('active')) {
        this.open();
      }
    } catch (error) {
      console.error('Error updating cart contents:', error);
      // Fallback to page reload if something goes wrong
      window.location.reload();
    }
  }

  /**
   * Parses an HTML string and returns a DOM element.
   * This is a helper function to support renderContents.
   * @param {string} html - The HTML string to parse.
   * @param {string} [selector='.shopify-section'] - The selector for the element to return.
   * @returns {HTMLElement}
   */
  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  /**
   * Sets the active element for focus trapping.
   * @param {HTMLElement} element - The element to set as active.
   */
  setActiveElement(element) {
    this.activeElement = element;
  }
}

// Only define the custom element if it hasn't been defined already
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

/**
 * This class likely works with a parent CartItems class to define which sections
 * to fetch from the API. While the renderContents method above no longer iterates
 * over sections, this class is kept for compatibility with the broader theme structure.
 * The logic in the new renderContents manually targets the required sections.
 */
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

// Only define the custom element if it hasn't been defined already
if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
